"use client";

import { useEffect, useRef, useSyncExternalStore } from "react";

/** Brand suffix appended to every Hub page title. */
export const HUB_TITLE_SUFFIX = "WTC Hub";

/**
 * Compose a full document title: `"<title> · WTC Hub"`, or just the brand when
 * there's no page-specific title. Exported so server metadata and tests can
 * build the identical string.
 */
export function composePageTitle(title?: string | null): string {
  const trimmed = typeof title === "string" ? title.trim() : "";
  return trimmed ? `${trimmed} · ${HUB_TITLE_SUFFIX}` : HUB_TITLE_SUFFIX;
}

// ---------------------------------------------------------------------------
// Detail-title store (single source for the page-title authority)
//
// Hub's route segments are `"use client"` (can't export Next `metadata`), so the
// document title is driven at runtime by ONE authority — PageTitleManager. Detail
// surfaces don't write `document.title` themselves (two writers race each other
// AND Next's metadata); instead they publish their record name here and the
// single authority renders it. A module singleton (not context) keeps it
// provider-free.
//
// Multiple publishers can be mounted at once: a detail MODAL (opened from a list
// via `?modal=…-detail`) can also stack on top of a full-page detail (e.g. click
// a contact from a company page). So the store keeps one entry per live publisher
// and the MOST-RECENTLY-published non-null title wins — the record on top. When a
// publisher unmounts (modal closes) its entry is dropped and the title falls back
// to the next most-recent publisher (the page underneath), then to the route
// section title.
// ---------------------------------------------------------------------------
interface TitleEntry {
  seq: number;
  title: string;
}

let seqCounter = 0;
const entries = new Map<number, TitleEntry>();
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

/** The most-recently-published non-null title, or null when none is active. */
function resolveDetailTitle(): string | null {
  let best: TitleEntry | null = null;
  for (const entry of entries.values()) {
    if (!best || entry.seq > best.seq) best = entry;
  }
  return best ? best.title : null;
}

/**
 * Publish (or clear, with null) one publisher's title, keyed by a stable id.
 * A non-null value that differs from the publisher's current entry becomes the
 * newest (winning) title; re-publishing the same value is a no-op so a lower
 * publisher doesn't spuriously jump above an active modal on unrelated renders.
 */
function setDetailPageTitleEntry(id: number, title: string | null): void {
  const next = title && title.trim() ? title.trim() : null;
  const prev = entries.get(id);

  if (next === null) {
    if (!prev) return;
    entries.delete(id);
  } else {
    if (prev && prev.title === next) return;
    entries.set(id, { seq: ++seqCounter, title: next });
  }
  emit();
}

function subscribeDetailTitle(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Read the current detail-page title (null when none active). */
export function useDetailPageTitle(): string | null {
  return useSyncExternalStore(subscribeDetailTitle, resolveDetailTitle, () => null);
}

/**
 * Publish a detail surface's title (e.g. `"Libby Lytle · Contact"`) to the single
 * title authority for this component's lifetime — used by both full-page detail
 * (UniversalDetailPage) and the query-param detail MODAL
 * (UniversalDetailModalContainer). Pass null while the record is still loading so
 * the route-section fallback ("Contacts") stays. Clears on unmount so closing a
 * modal or navigating away doesn't leave a stale name.
 */
export function usePublishDetailPageTitle(title: string | null | undefined): void {
  const value = typeof title === "string" && title.trim() ? title.trim() : null;

  // Stable per-instance id so nested publishers (modal over page) don't collide.
  const idRef = useRef<number | null>(null);
  if (idRef.current === null) idRef.current = ++seqCounter;
  const id = idRef.current;

  useEffect(() => {
    setDetailPageTitleEntry(id, value);
    return () => setDetailPageTitleEntry(id, null);
  }, [id, value]);
}
